import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, serverTimestamp, updateDoc, arrayUnion } from 'firebase/firestore';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import LoginBackground from '../components/LoginBackground';
import BBALogoLockup from '../components/BBALogoLockup';
import { getIpLocation, getIpLocationWithin } from '../services/ipLocation';
import { logStaffLogin } from '../services/staffActivityLog';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const emailChanged = searchParams.get('emailChanged') === '1';

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState(location.state?.email || '');
  // Set by /signup when someone reopens a sign-up link they already used
  const signupLinkNotice = {
    used: "You've already set up your account. Sign in below to get back to your portal.",
    usedOrExpired: "This sign-up link has already been used or has expired. If you've already created your account, sign in below. If not, ask your Builders Breakthrough team for a new link.",
  }[location.state?.notice];
  const [password, setPassword] = useState('');
  const [error, setError] = useState(location.state?.error || '');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');

  // Warm the IP location cache so the login history entry can include it
  useEffect(() => {
    getIpLocation();
  }, []);

  // Get the redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '/crm';

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setForgotPasswordLoading(true);
    setError('');
    setForgotPasswordMessage('');

    try {
      // Initialize Firebase Functions
      const functions = getFunctions();
      const sendPasswordResetEmailFunction = httpsCallable(functions, 'sendPasswordResetEmail');
      
      // Call the Cloud Function to send password reset email
      const result = await sendPasswordResetEmailFunction({ email });
      const { success, message } = result.data;
      
      if (success) {
        setForgotPasswordMessage(message || 'If an account exists for that email, a password reset link is on its way. Please check your inbox.');
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if user document exists in users collection (portal users)
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      // Check if user exists in internal_users collection (CRM users)
      const internalUserDocRef = doc(db, 'internal_users', user.uid);
      const internalUserDoc = await getDoc(internalUserDocRef);

      let isNewStaff = false;
      if (!userDoc.exists() && !internalUserDoc.exists()) {
        // No profile yet: only a pending team invite for this (verified) email
        // makes a CRM user, and the server decides that.
        try {
          await httpsCallable(getFunctions(), 'acceptInvite')({ byEmail: true });
          await user.getIdToken(true);
          isNewStaff = true;
        } catch (inviteError) {
          await auth.signOut();
          setError('You need a valid invitation to access this platform. Please contact your administrator for an invite.');
          return;
        }
      } else if (userDoc.exists()) {
        // Portal user exists - update lastLogin and append to loginHistory.
        // IP location was prefetched on mount; cap the wait so sign-in never stalls.
        const timestamp = new Date().toISOString();
        const ipLocation = await getIpLocationWithin(1500);
        await updateDoc(userDocRef, {
          lastLogin: serverTimestamp(),
          loginHistory: arrayUnion({ timestamp, ...(ipLocation ? { location: ipLocation } : {}) })
        });
      } else if (internalUserDoc.exists()) {
        // Internal user exists - update lastLogin
        await updateDoc(internalUserDocRef, {
          lastLogin: serverTimestamp()
        });
        // Log staff CRM login
        const internalData = internalUserDoc.data();
        logStaffLogin({
          uid: user.uid,
          email: user.email,
          name: [internalData.firstName, internalData.lastName].filter(Boolean).join(' ') || null,
        });
      }

      // Determine the correct redirect path based on user type
      let redirectPath = from;
      
      // Always determine the correct dashboard based on user type
      if (internalUserDoc.exists() || isNewStaff) {
        // Internal user - always redirect to CRM
        redirectPath = '/crm';
        // Log new staff login (acceptInvite just created their profile)
        if (isNewStaff) {
          logStaffLogin({ uid: user.uid, email: user.email, name: null });
        }
      } else if (userDoc.exists()) {
        // Portal user - check linked account type to determine routing
        const userData = userDoc.data();
        if (userData.accountId) {
          try {
            const accountDoc = await getDoc(doc(db, 'accounts', userData.accountId));
            if (accountDoc.exists()) {
              const accountData = accountDoc.data();
              if (accountData.type === 'Clients' || accountData.type === 'clients') {
                // This is a client account - redirect to portal dashboard
                redirectPath = '/portal';
              } else {
                // This is a lead account - redirect to start dashboard
                redirectPath = '/start';
              }
            } else {
              // No linked account found - redirect to start dashboard
              redirectPath = '/start';
            }
          } catch (error) {
            console.error('Error fetching linked account:', error);
            // If we can't fetch the account, default to start
            redirectPath = '/start';
          }
        } else {
          // No linked account - redirect to start dashboard
          redirectPath = '/start';
        }
      } else {
        // New user - default to start
        redirectPath = '/start';
      }
      
      // Navigate to the correct path
      navigate(redirectPath, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      let msg = "Something went wrong. Please try again.";

      switch (error.code) {
        case "auth/user-not-found":
          msg = "No account found with this email.";
          break;
        case "auth/wrong-password":
          msg = "Incorrect password. Please try again.";
          break;
        case "auth/invalid-email":
          msg = "Please enter a valid email address.";
          break;
        case "auth/too-many-requests":
          msg = "Too many failed attempts. Please try again later.";
          break;
        case "auth/invalid-credential":
          msg = "Invalid login. Please check your credentials.";
          break;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <LoginBackground />

      {/* Login Content */}
      <div className="w-full max-w-md relative z-10">
        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo and Title Section */}
          <div className="text-center mb-8">
            <BBALogoLockup className="mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-600">Please sign in to your account</p>
          </div>
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {forgotPasswordMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">{forgotPasswordMessage}</p>
                </div>
              </div>
            </div>
          )}

          {signupLinkNotice && !error && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">{signupLinkNotice}</p>
                </div>
              </div>
            </div>
          )}

          {emailChanged && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">Your email has been updated. Please sign in with your new email address.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={forgotPasswordLoading || !email}
                  className="text-sm text-red-600 hover:text-red-500 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Forgot password?'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </form>


        </div>
      </div>
    </div>
  );
};

export default LoginPage; 